import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, GraduationCap, MapPin, Clock, ExternalLink, Filter, Search, Building2, BookOpen } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Formation type icons
const TYPE_ICONS = {
  university: GraduationCap,
  haute_ecole: BookOpen,
  promotion_sociale: Building2,
  ifapme: Building2,
  certification: BookOpen,
};

// Formation type colors
const TYPE_COLORS = {
  university: 'bg-purple-100 text-purple-700 border-purple-200',
  haute_ecole: 'bg-blue-100 text-blue-700 border-blue-200',
  promotion_sociale: 'bg-green-100 text-green-700 border-green-200',
  ifapme: 'bg-orange-100 text-orange-700 border-orange-200',
  certification: 'bg-pink-100 text-pink-700 border-pink-200',
};

export default function FormationsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [language, setLanguage] = useState(searchParams.get('lang') || 'fr');
  const [formations, setFormations] = useState([]);
  const [filteredFormations, setFilteredFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [availableTypes, setAvailableTypes] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);

  // Labels
  const labels = {
    fr: {
      title: 'Explorer les formations',
      subtitle: 'Découvrez les formations belges liées à vos centres d\'intérêt',
      search: 'Rechercher une formation ou institution...',
      filters: 'Filtres',
      allTypes: 'Tous les types',
      allRegions: 'Toutes les régions',
      duration: 'Durée',
      location: 'Localisation',
      linkedCareers: 'Métiers liés',
      visitWebsite: 'Voir le site',
      noResults: 'Aucune formation trouvée',
      noResultsDesc: 'Essayez de modifier vos filtres',
      back: 'Retour',
      formationsCount: 'formations trouvées',
      languageFilter: 'Langue des formations',
      french: 'Français',
      dutch: 'Néerlandais',
    },
    nl: {
      title: 'Opleidingen verkennen',
      subtitle: 'Ontdek Belgische opleidingen die aansluiten bij jouw interesses',
      search: 'Zoek een opleiding of instelling...',
      filters: 'Filters',
      allTypes: 'Alle types',
      allRegions: 'Alle regio\'s',
      duration: 'Duur',
      location: 'Locatie',
      linkedCareers: 'Gerelateerde beroepen',
      visitWebsite: 'Bezoek website',
      noResults: 'Geen opleidingen gevonden',
      noResultsDesc: 'Probeer je filters aan te passen',
      back: 'Terug',
      formationsCount: 'opleidingen gevonden',
      languageFilter: 'Taal van opleidingen',
      french: 'Frans',
      dutch: 'Nederlands',
    }
  };

  const t = labels[language];

  // Fetch formations data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch formations
        const formationsRes = await axios.get(`${API}/api/formations?language=${language}`);
        setFormations(formationsRes.data.formations || []);
        setFilteredFormations(formationsRes.data.formations || []);
        
        // Fetch filter options
        const typesRes = await axios.get(`${API}/api/formations/types?language=${language}`);
        setAvailableTypes(typesRes.data.types || []);
        
        const regionsRes = await axios.get(`${API}/api/formations/regions?language=${language}`);
        setAvailableRegions(regionsRes.data.regions || []);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching formations:', err);
        setError('Erreur lors du chargement des formations');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [language]);

  // Apply filters
  useEffect(() => {
    let results = [...formations];
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      results = results.filter(f => 
        f.name.toLowerCase().includes(query) ||
        f.institution.toLowerCase().includes(query) ||
        f.careers?.some(c => c.toLowerCase().includes(query))
      );
    }
    
    // Type filter
    if (selectedType) {
      results = results.filter(f => f.type === selectedType);
    }
    
    // Region filter
    if (selectedRegions.length > 0) {
      results = results.filter(f => 
        f.regions?.some(r => selectedRegions.includes(r))
      );
    }
    
    setFilteredFormations(results);
  }, [formations, searchQuery, selectedType, selectedRegions]);

  const toggleRegion = (region) => {
    setSelectedRegions(prev => 
      prev.includes(region)
        ? prev.filter(r => r !== region)
        : [...prev, region]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F5F0FF] to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A4187A] mx-auto"></div>
          <p className="mt-4 text-[#1D1D1D]/60">Chargement des formations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F0FF] to-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-[#A4187A] hover:text-[#4A0080] transition"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>{t.back}</span>
            </button>
            
            {/* Language switcher */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setLanguage('fr')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                  language === 'fr' 
                    ? 'bg-[#A4187A] text-white' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="lang-fr-btn"
              >
                FR
              </button>
              <button
                onClick={() => setLanguage('nl')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                  language === 'nl' 
                    ? 'bg-[#A4187A] text-white' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="lang-nl-btn"
              >
                NL
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#1D1D1D] mb-2 flex items-center justify-center gap-3">
            <GraduationCap className="w-8 h-8 text-[#A4187A]" />
            {t.title}
          </h1>
          <p className="text-[#1D1D1D]/60">{t.subtitle}</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-[#A4187A]" />
            <h2 className="font-semibold text-[#1D1D1D]">{t.filters}</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t.search}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A4187A]/20 focus:border-[#A4187A]"
                data-testid="search-input"
              />
            </div>
            
            {/* Type filter */}
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A4187A]/20 focus:border-[#A4187A] bg-white"
              data-testid="type-filter"
            >
              <option value="">{t.allTypes}</option>
              {availableTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            
            {/* Region buttons */}
            <div className="flex flex-wrap gap-2">
              {availableRegions.map(region => (
                <button
                  key={region.value}
                  onClick={() => toggleRegion(region.label)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                    selectedRegions.includes(region.label)
                      ? 'bg-[#A4187A] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`region-filter-${region.value}`}
                >
                  {region.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results count */}
        <p className="text-sm text-[#1D1D1D]/60 mb-4">
          {filteredFormations.length} {t.formationsCount}
        </p>

        {/* Formations grid */}
        {filteredFormations.length === 0 ? (
          <div className="text-center py-16">
            <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#1D1D1D] mb-2">{t.noResults}</h3>
            <p className="text-[#1D1D1D]/60">{t.noResultsDesc}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFormations.map(formation => {
              const TypeIcon = TYPE_ICONS[formation.type] || GraduationCap;
              const typeColor = TYPE_COLORS[formation.type] || 'bg-gray-100 text-gray-700';
              
              return (
                <div 
                  key={formation.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition group"
                  data-testid={`formation-card-${formation.id}`}
                >
                  {/* Header */}
                  <div className="p-5 border-b border-gray-100">
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${typeColor} mb-3`}>
                      <TypeIcon className="w-3.5 h-3.5" />
                      {formation.type_label}
                    </div>
                    <h3 className="font-bold text-[#1D1D1D] group-hover:text-[#A4187A] transition line-clamp-2">
                      {formation.name}
                    </h3>
                    <p className="text-sm text-[#1D1D1D]/70 mt-1">{formation.institution}</p>
                  </div>
                  
                  {/* Details */}
                  <div className="p-5 space-y-3">
                    <div className="flex items-center gap-2 text-sm text-[#1D1D1D]/70">
                      <Clock className="w-4 h-4 text-[#A4187A]" />
                      <span>{t.duration}: {formation.duration}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#1D1D1D]/70">
                      <MapPin className="w-4 h-4 text-[#FB6823]" />
                      <span>{formation.city}</span>
                    </div>
                    
                    {/* Regions tags */}
                    <div className="flex flex-wrap gap-1">
                      {formation.regions?.map((region, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                          {region}
                        </span>
                      ))}
                    </div>
                    
                    {/* Linked careers */}
                    {formation.careers && formation.careers.length > 0 && (
                      <div className="pt-2 border-t border-gray-100">
                        <p className="text-xs text-[#1D1D1D]/50 mb-1">{t.linkedCareers}:</p>
                        <p className="text-sm text-[#1D1D1D]/70 line-clamp-2">
                          {formation.careers.slice(0, 3).join(', ')}
                          {formation.careers.length > 3 && '...'}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Footer */}
                  <div className="px-5 pb-5">
                    <a
                      href={formation.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#A4187A] text-white rounded-lg font-medium hover:bg-[#4A0080] transition"
                      data-testid={`visit-website-${formation.id}`}
                    >
                      <ExternalLink className="w-4 h-4" />
                      {t.visitWebsite}
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
